import React, { FocusEvent } from 'react';
import { Field } from 'formik';
import { BaseFieldProps, FormGroup, TextboxField, AddressField, SelectDropdownField } from '@tradingblock/components';
import { AllStates, getGroupedCountryOptions } from '@tradingblock/types';
import { PersonType } from '../../types';
import { NAME_SUFFIX_OPTIONS } from '../../types/Enums';
import config from '../../config';
import { useData } from '../../state/selectors';

interface PersonFieldProps extends BaseFieldProps {
  personType: PersonType;
  excludeEmailFields?: boolean;
}

export const PersonField: React.FC<PersonFieldProps> = ({
  id,
  personType,
  labelPrefix,
  form,
  excludeEmailFields = false,
}) => {
  const apiToken = useData(d => d.ui.apiToken);
  const prefix = id ? `${id}.` : '';
  const { getFieldProps, setFieldValue } = form;
  const clearer = form.values.clearer;

  return (
    <>
      <FormGroup>
        <Field
          component={TextboxField}
          id={`${prefix}firstName`}
          defaultLabelId="firstName"
          autoComplete="given-name"
        />
      </FormGroup>
      {personType !== PersonType.TrustedContact && (
        <FormGroup>
          <Field
            component={TextboxField}
            id={`${prefix}middleName`}
            defaultLabelId="middleName"
            autoComplete="additional-name"
          />
        </FormGroup>
      )}
      <FormGroup>
        <Field component={TextboxField} id={`${prefix}lastName`} defaultLabelId="lastName" autoComplete="family-name" />
      </FormGroup>
      {personType !== PersonType.TrustedContact && (
        <FormGroup>
          <Field
            component={SelectDropdownField}
            id={`${prefix}suffix`}
            defaultLabelId="suffix"
            optionsDefaultLabelId="suffix"
            options={NAME_SUFFIX_OPTIONS.map(option => option.value)}
            isSearchable={false}
            isClearable
          />
        </FormGroup>
      )}

      {personType === PersonType.AccountHolder && !excludeEmailFields && (
        <>
          <FormGroup>
            <Field
              component={TextboxField}
              id={`${prefix}email`}
              type="email"
              inputMode="email"
              defaultLabelId="email"
              autoComplete="email"
            />
          </FormGroup>
          <FormGroup>
            <Field
              component={TextboxField}
              id={`${prefix}emailConfirm`}
              type="email"
              inputMode="email"
              defaultLabelId="emailConfirm"
              autoComplete="email"
            />
          </FormGroup>
        </>
      )}

      {personType !== PersonType.AccountHolder && (
        <>
          {personType === PersonType.TradeAuthorization && (
            <FormGroup>
              <Field
                component={TextboxField}
                id={`${prefix}email`}
                type="email"
                inputMode="email"
                defaultLabelId="email"
                autoComplete="email"
              />
            </FormGroup>
          )}

          <Field
            component={AddressField}
            id={`${prefix}address`}
            labelPrefix={labelPrefix}
            tradingApiUrl={config.tradingApi}
            apiToken={apiToken}
            countryProps={{
              options: getGroupedCountryOptions(clearer),
              defaultLabelId: 'address.country',
              optionsDefaultLabelId: 'country',
              labelPrefix,
            }}
            stateProps={{
              options: AllStates,
              defaultLabelId: 'address.state',
              optionsDefaultLabelId: 'state',
              labelPrefix,
            }}
          />
        </>
      )}
    </>
  );
};
